import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { centralApi } from '@/services/centralApi';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

const CAP_LABELS = {
  auto_reply_new_contacts: 'Responder automaticamente novos contatos',
  consult_orders: 'Consultar pedidos',
  consult_production: 'Consultar produção',
  consult_deliveries: 'Consultar entregas',
  create_quote_request: 'Criar solicitação de orçamento',
  accept_images: 'Aceitar imagens',
  accept_documents: 'Aceitar documentos',
  accept_audio: 'Aceitar áudio',
  transcribe_audio: 'Transcrever áudio',
  transfer_on_doubt: 'Transferir quando houver dúvida',
  transfer_when_requested: 'Transferir quando cliente pedir atendente',
};

export default function CentralConfigIA() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => { centralApi.getAiSettings().then(setCfg); }, []);

  const save = async () => {
    try {
      const updated = await centralApi.updateAiSettings(cfg);
      setCfg(updated);
      toast.success('Configurações salvas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  return (
    <Layout>
    <div className="space-y-4" data-testid="central-config-ia">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Configurações da IA</h1>
        <p className="text-sm text-muted-foreground">Comportamento da assistente virtual no WhatsApp</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Identidade e mensagens</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-medium">Nome da assistente</label>
              <Input value={cfg.assistant_name || ''} onChange={(e) => setCfg({ ...cfg, assistant_name: e.target.value })} data-testid="ia-assistant-name" />
            </div>
            <div>
              <label className="text-xs font-medium">Mensagem de boas-vindas</label>
              <Textarea rows={3} value={cfg.welcome_message || ''} onChange={(e) => setCfg({ ...cfg, welcome_message: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium">Mensagem fora do horário</label>
              <Textarea rows={3} value={cfg.out_of_hours_message || ''} onChange={(e) => setCfg({ ...cfg, out_of_hours_message: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium">Tom de voz</label>
              <Select value={cfg.tone || 'profissional_cordial'} onValueChange={(v) => setCfg({ ...cfg, tone: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="profissional_cordial">Profissional e cordial</SelectItem>
                  <SelectItem value="direto">Direto</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="informal">Informal amigável</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Atendimento</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-medium">Horário de atendimento</label>
              <Input value={cfg.business_hours || ''} onChange={(e) => setCfg({ ...cfg, business_hours: e.target.value })} />
            </div>
            <div className="flex items-center justify-between border rounded-md p-3">
              <div>
                <p className="text-sm font-medium">Permitir IA fora do horário</p>
                <p className="text-xs text-muted-foreground">A assistente continua respondendo fora do expediente.</p>
              </div>
              <Switch checked={!!cfg.allow_ai_off_hours} onCheckedChange={(v) => setCfg({ ...cfg, allow_ai_off_hours: v })} />
            </div>
            <div className="flex items-center justify-between border rounded-md p-3">
              <div>
                <p className="text-sm font-medium">Transferir automaticamente para humano</p>
                <p className="text-xs text-muted-foreground">Ao detectar pedidos como "quero falar com atendente".</p>
              </div>
              <Switch checked={!!cfg.auto_transfer_to_human} onCheckedChange={(v) => setCfg({ ...cfg, auto_transfer_to_human: v })} />
            </div>
            <div>
              <label className="text-xs font-medium">Mensagem de transferência</label>
              <Textarea rows={3} value={cfg.handoff_message || ''} onChange={(e) => setCfg({ ...cfg, handoff_message: e.target.value })} />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader><CardTitle className="text-base">Autonomia da IA</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs font-medium">Nível</label>
            <Select value={cfg.level || 'restrito'} onValueChange={(v) => setCfg({ ...cfg, level: v })}>
              <SelectTrigger data-testid="ia-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="restrito">Restrito — só dados cadastrados e ferramentas autorizadas</SelectItem>
                <SelectItem value="moderado">Moderado — pode coletar orçamentos e fazer perguntas</SelectItem>
                <SelectItem value="avancado">Avançado — preparado para futuras ações autorizadas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(CAP_LABELS).map(([k, label]) => (
              <div key={k} className="flex items-center justify-between border rounded-md p-2">
                <span className="text-sm">{label}</span>
                <Switch
                  checked={!!cfg.capabilities?.[k]}
                  onCheckedChange={(v) => setCfg({ ...cfg, capabilities: { ...(cfg.capabilities || {}), [k]: v } })}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader><CardTitle className="text-base">Instruções da IA (prompt)</CardTitle></CardHeader>
        <CardContent>
          <Textarea rows={14} value={cfg.prompt || ''} onChange={(e) => setCfg({ ...cfg, prompt: e.target.value })} data-testid="ia-prompt" />
        </CardContent>
      </Card>

      <div className="flex justify-end sticky bottom-2">
        <Button size="lg" onClick={save} data-testid="ia-save">Salvar configurações</Button>
      </div>
    </div>
    </Layout>
  );
}
